import { BigNum } from '@drift-labs/sdk';

export interface TpSlValidationResult {
	invalid: boolean;
	reason: 'entry' | 'immediate' | 'reference' | null;
	messageTitle?: string;
}

/**
 * Validates take profit and stop loss order prices based on position direction,
 * entry price, and current market conditions.
 *
 * @param priceBigNum - The order price to validate
 * @param orderType - The type of order (should include 'takeProfit' or 'stop' for TP/SL validation)
 * @param positionDirection - The position direction ('long' or 'short')
 * @param entryPrice - The position entry price
 * @param markPrice - The current mark/reference price
 * @param allowTrailingStopLoss - Whether trailing stop loss is allowed (defaults to false)
 * @returns Validation result with invalid flag, reason, and error message
 */
export const validateTpSlOrder = (
	priceBigNum: BigNum,
	orderType: string,
	positionDirection: 'long' | 'short',
	entryPrice: BigNum,
	markPrice: BigNum,
	allowTrailingStopLoss = false
): TpSlValidationResult => {
	const isTakeProfit =
		orderType.includes('takeProfit') || orderType.includes('TakeProfit');
	const isStopLoss = orderType.includes('stop') || orderType.includes('Stop');
	const positionIsLong = positionDirection === 'long';

	if (!isTakeProfit && !isStopLoss) {
		return { invalid: false, reason: null };
	}

	if (positionIsLong) {
		if (isTakeProfit) {
			// For long positions, take profit must be above current asset price (reference price)
			if (priceBigNum.lt(markPrice)) {
				return {
					invalid: true,
					reason: 'reference',
					messageTitle: 'Take profit price must be above the reference price',
				};
			}
			// For long positions, if entry price is above mark price, allow take profit below entry price
			// Otherwise, take profit must be above entry price
			if (entryPrice.lte(markPrice) && priceBigNum.lt(entryPrice)) {
				return {
					invalid: true,
					reason: 'entry',
					messageTitle: 'Take profit price must be above the entry price',
				};
			}
			return { invalid: false, reason: null };
		}

		if (isStopLoss) {
			// For long positions, stop loss must always be below current asset price (reference price)
			if (priceBigNum.gt(markPrice)) {
				return {
					invalid: true,
					reason: 'reference',
					messageTitle: 'Stop loss price must be below the reference price',
				};
			}
			// For long positions, if entry price is above or equal to mark price (underwater/break-even),
			// stop loss must be below entry price
			if (entryPrice.gte(markPrice) && priceBigNum.gt(entryPrice)) {
				return {
					invalid: true,
					reason: 'entry',
					messageTitle: 'Stop loss price must be below the entry price',
				};
			}
			// Immediate execution if SL >= mark and < entry (only for non-profitable positions)
			if (
				entryPrice.gte(markPrice) &&
				priceBigNum.gte(markPrice) &&
				priceBigNum.lte(entryPrice)
			) {
				return {
					invalid: true,
					reason: 'immediate',
					messageTitle:
						"This stop loss would execute immediately because it's at or above the current reference price",
				};
			}
			return { invalid: false, reason: null };
		}
	} else {
		// Short position
		if (isTakeProfit) {
			// For short positions, take profit must be below current asset price (reference price)
			if (priceBigNum.gt(markPrice)) {
				return {
					invalid: true,
					reason: 'reference',
					messageTitle: 'Take profit price must be below the reference price',
				};
			}
			// For short positions, if entry price is below mark price, allow take profit above entry price
			// Otherwise, take profit must be below entry price
			if (entryPrice.gte(markPrice) && priceBigNum.gt(entryPrice)) {
				return {
					invalid: true,
					reason: 'entry',
					messageTitle: 'Take profit price must be below the entry price',
				};
			}
			return { invalid: false, reason: null };
		}

		if (isStopLoss) {
			// For short positions, stop loss must be above current asset price (reference price)
			if (priceBigNum.lt(markPrice)) {
				return {
					invalid: true,
					reason: 'reference',
					messageTitle: 'Stop loss price must be above the reference price',
				};
			}
			// For short positions, if entry price is below mark price and trailing stop loss not allowed,
			// stop loss must be above entry price
			if (
				!allowTrailingStopLoss &&
				entryPrice.lt(markPrice) &&
				priceBigNum.lt(entryPrice)
			) {
				return {
					invalid: true,
					reason: 'entry',
					messageTitle: 'Stop loss price must be above the entry price',
				};
			}
			// Immediate execution if SL <= mark and >= entry
			if (priceBigNum.lte(markPrice) && priceBigNum.gte(entryPrice)) {
				return {
					invalid: true,
					reason: 'immediate',
					messageTitle:
						"This stop loss would execute immediately because it's at or below the current reference price",
				};
			}
			return {
				invalid: allowTrailingStopLoss ? false : priceBigNum.lt(entryPrice),
				reason: allowTrailingStopLoss ? null : 'entry',
				messageTitle: allowTrailingStopLoss
					? undefined
					: 'Stop loss price must be above the entry price',
			};
		}
	}

	return { invalid: false, reason: null };
};

/**
 * Checks if a stop loss order is beyond the estimated liquidation price
 *
 * @param priceBigNum - The stop loss price to check
 * @param orderType - The order type (must include 'stop' for validation)
 * @param liquidationPrice - The estimated liquidation price
 * @param positionDirection - The position direction ('long' or 'short')
 * @returns True if stop loss is beyond liquidation price
 */
export const isStopLossBeyondLiquidationPrice = (
	priceBigNum: BigNum,
	orderType: string,
	liquidationPrice: BigNum,
	positionDirection: 'long' | 'short'
): boolean => {
	if (!orderType.includes('stop') && !orderType.includes('Stop')) {
		return false;
	}

	const positionIsLong = positionDirection === 'long';

	if (positionIsLong) {
		// For long positions, stop loss should be above liquidation price
		return priceBigNum.lt(liquidationPrice);
	} else {
		// For short positions, stop loss should be below liquidation price
		return priceBigNum.gt(liquidationPrice);
	}
};
